import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";

/** tenantAdmin: uživatelé tenanta + pozvánky do vlastního tenanta. */

export interface TenantUser {
  user_id: string;
  display_name: string;
  email: string;
  role: string;
  deactivated_at: string | null;
}

export interface TenantUsersResponse {
  users: TenantUser[];
}

export interface TenantInvitation {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  onboarded_at: string | null;
}

export interface TenantInvitationsResponse {
  rows: TenantInvitation[];
}

export interface CreateTenantInvitationRequest {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

const KEY_USERS = ["tenant", "users"] as const;
const KEY_INVITES = ["tenant", "user-invitations"] as const;

export function useTenantUsers() {
  return useQuery({
    queryKey: KEY_USERS,
    queryFn: () => apiCall<TenantUsersResponse>("/api/tenant/users"),
    staleTime: 60_000,
  });
}

export function useTenantInvitations() {
  return useQuery({
    queryKey: KEY_INVITES,
    queryFn: () => apiCall<TenantInvitationsResponse>("/api/tenant/user-invitations"),
    staleTime: 30_000,
  });
}

export function useCreateTenantInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTenantInvitationRequest) =>
      apiCall("/api/tenant/user-invitations", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: KEY_INVITES });
    },
  });
}

export function useRevokeTenantInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiCall(`/api/tenant/user-invitations/${invitationId}/revoke`, { method: "POST" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: KEY_INVITES });
    },
  });
}
